import { useQuery } from '@tanstack/react-query'

import { previewDraftStepApiV1CampaignsCampaignIdStepsPreviewPost } from '#/shared/api/generated/communication/theCargoCommunication'
import type { CampaignChannel } from '#/entities/campaign'

import { CONDITION_TO_API } from './use-campaign-add-step'
import type { CampaignStepCondition } from './use-campaign-add-step'

/**
 * `body` is required and must be non-empty, but the picker runs before anybody
 * has written one. The line we read back — "Step 5 · SMS · 2 days after step 4
 * · 164 eligible" — is built from the channel, the timing and the enrolment,
 * none of which the body touches, so a placeholder satisfies the schema
 * without changing the answer. Everything the body *does* decide (characters,
 * segments, encoding) is deliberately ignored here; the composer asks for
 * those again with the real message.
 */
const PLACEHOLDER_BODY = '…'

interface CampaignStepPreviewOptions {
  campaignId: string
  /** Only while the picker is open — the dialog opens closed. */
  enabled: boolean
  channel: CampaignChannel
  /** Null while Custom has no valid day count — nothing to preview. */
  delayMinutes: number | null
  conditions: CampaignStepCondition[]
}

/**
 * The picker's footer line, dry-run against the campaign as it stands.
 *
 * `POST /campaigns/{id}/steps/preview` stores nothing, which is what makes it
 * safe to fire on every chip: it resolves the step the picker describes
 * without adding it. The line is the service's sentence, rendered as sent.
 */
export function useCampaignStepPreview({
  campaignId,
  enabled,
  channel,
  delayMinutes,
  conditions,
}: CampaignStepPreviewOptions) {
  const onlyIf = conditions.map((condition) => CONDITION_TO_API[condition])
  // A delay the service has no unit for cannot be previewed, so the line stays
  // absent rather than describing a step that could not be added either.
  const canPreview = enabled && delayMinutes != null

  const query = useQuery({
    queryKey: [
      '/api/v1/campaigns/{campaignId}/steps/preview',
      campaignId,
      channel,
      delayMinutes,
      onlyIf,
    ],
    queryFn: ({ signal }) =>
      previewDraftStepApiV1CampaignsCampaignIdStepsPreviewPost(
        campaignId,
        {
          channel,
          delay_minutes: delayMinutes ?? 0,
          only_if: onlyIf,
          body: PLACEHOLDER_BODY,
        },
        signal,
      ),
    enabled: canPreview,
    // The picker is transient and the eligible count moves with enrolment, so
    // reopening it should ask again rather than show a stale headcount.
    staleTime: 0,
    // A failed dry run is not worth a toast — the line simply does not appear.
    retry: false,
  })

  return {
    summary: query.data?.data.summary ?? undefined,
    isLoading: canPreview && query.isFetching,
  }
}
